import { useState, type FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { ArrowLeft, Search, Printer, Users } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventUserResult {
  token: string
  firstName: string | null
  lastName: string | null
  organization: string | null
  position: string | null
  title: string | null
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  search: string
  eventUsers: EventUserResult[]
  urls: AppShellProps['urls'] & {
    result: string
    eventUsers: string
    badgeBase: string
  }
  translations: AppShellProps['translations'] & {
    searchResults: string
    print: string
    noResults: string
    searchPlaceholder: string
    search: string
    back: string
    organization: string
    position: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Result(props: Props) {
  const { event, search: initialSearch, eventUsers, urls, translations: t, ...shell } = props
  const [query, setQuery] = useState(initialSearch)

  function handleSearch(e: FormEvent) {
    e.preventDefault()
    if (!query.trim()) return
    router.visit(urls.result, { data: { search: query.trim() }, preserveState: true })
  }

  function handlePrintBadge(token: string) {
    // Open badge in new window for printing
    const badgeUrl = `${urls.badgeBase}?id=${token}`
    const win = window.open(badgeUrl, '_blank', 'width=600,height=600')
    if (win) {
      win.addEventListener('load', () => {
        setTimeout(() => win.print(), 500)
      })
    }
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.searchResults} — ${event.name}`} />

      <div className="mx-auto max-w-2xl py-10 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 text-[#91918E]"
          onClick={() => router.visit(urls.eventUsers)}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.back}
        </Button>

        <h1 className="text-xl font-semibold text-[#37352F] mb-6">{t.searchResults}</h1>

        {/* Search form */}
        <form onSubmit={handleSearch} className="mb-6 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#91918E]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="pl-9"
            />
          </div>
          <Button type="submit" className="bg-[#2383E2] hover:bg-[#1b6ec2]">
            {t.search}
          </Button>
        </form>

        {/* Results */}
        {eventUsers.length > 0 ? (
          <div className="space-y-3">
            {eventUsers.map((eu) => (
              <Card key={eu.token}>
                <CardContent className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-sm font-medium text-[#37352F]">
                      {[eu.title, eu.firstName, eu.lastName].filter(Boolean).join(' ')}
                    </p>
                    <div className="mt-1 flex gap-4 text-xs text-[#91918E]">
                      {eu.organization && <span>{t.organization}: {eu.organization}</span>}
                      {eu.position && <span>{t.position}: {eu.position}</span>}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePrintBadge(eu.token)}
                  >
                    <Printer className="mr-1 h-4 w-4" />
                    {t.print}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : initialSearch ? (
          <div className="text-center py-12 text-[#91918E]">
            <Users className="mx-auto mb-3 h-10 w-10" />
            <p className="text-sm">{t.noResults}</p>
          </div>
        ) : null}
      </div>
    </AppShell>
  )
}
